import { FormEvent, useState } from 'react'

import * as api from 'api/api'
import { Site, SiteInput, SiteStatus } from 'api/types'
import { useConfirm } from 'components/Confirm'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, IconButton, Sheet, Spinner, TextField } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

const STATUS_OPTIONS: SiteStatus[] = ['ACTIVE', 'PAUSED', 'CLOSED']

const siteStatusLabel = (status: SiteStatus) => {
  if (status === 'ACTIVE') return t('نشطة', 'Active')
  if (status === 'CLOSED') return t('مغلقة', 'Closed')
  return t('متوقفة', 'On hold')
}
const STATUS_BADGE_CLASS: Record<SiteStatus, string> = {
  ACTIVE: 'badge badge--complete',
  CLOSED: 'badge badge--missing',
  PAUSED: 'badge badge--draft',
}

const EMPTY_FORM: SiteInput = { engineerId: null, name: '', scope: '', shift: '', status: 'ACTIVE' }

const SiteForm = ({
  initial,
  engineers,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: Site
  engineers: { id: string; name: string }[]
  busy: boolean
  onCancel: () => void
  onSubmit: (input: SiteInput) => void
}) => {
  const [form, setForm] = useState<SiteInput>(() =>
    initial
      ? { engineerId: initial.engineerId ?? null, name: initial.name, scope: initial.scope ?? '', shift: initial.shift ?? '', status: initial.status }
      : EMPTY_FORM
  )

  const valid = form.name.trim()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    onSubmit({
      engineerId: form.engineerId || null,
      name: form.name.trim(),
      scope: form.scope?.trim() || null,
      shift: form.shift?.trim() || null,
      status: form.status,
    })
  }

  return (
    <Sheet
      title={initial ? t('تعديل منطقة عمل', 'Edit work area') : t('إضافة منطقة عمل', 'Add work area')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" form="site-form" variant="primary" busy={busy} disabled={!valid}>
            {initial ? t('حفظ', 'Save') : t('إضافة', 'Add')}
          </Button>
        </>
      }
    >
      <form id="site-form" className="form-grid" onSubmit={submit}>
        <TextField
          label={t('اسم منطقة العمل', 'Work area name')}
          className="span-2"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder={t('مثال: المبنى الرئيسي', 'Example: Main building')}
          autoFocus
          required
        />
        <TextField
          label={t('النطاق / المساحة', 'Scope / area')}
          className="span-2"
          value={form.scope ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, scope: event.target.value }))}
          placeholder={t('مثال: من الأرضي إلى الطابق الثاني · 3,400 م²', 'Example: Ground floor to the second floor · 3,400 m²')}
        />
        <div>
          <label className="field__label" htmlFor="site-engineer">
            {t('مهندس الموقع', 'Site Engineer')}
          </label>
          <select
            id="site-engineer"
            className="input"
            value={form.engineerId ?? ''}
            onChange={(event) => setForm((current) => ({ ...current, engineerId: event.target.value || null }))}
          >
            <option value="">{t('— غير مُسند —', '— Unassigned —')}</option>
            {engineers.map((engineer) => (
              <option key={engineer.id} value={engineer.id}>
                {engineer.name}
              </option>
            ))}
          </select>
        </div>
        <TextField
          label={t('الوردية', 'Shift')}
          value={form.shift ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, shift: event.target.value }))}
          placeholder={t('مثال: صباحية · 07:00–17:00', 'Example: Morning · 07:00–17:00')}
        />
        <div className="span-2">
          <label className="field__label" htmlFor="site-status">
            {t('الحالة', 'Status')} <span className="field__required">*</span>
          </label>
          <select
            id="site-status"
            className="input"
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as SiteStatus }))}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {siteStatusLabel(status)}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Sheet>
  )
}

/** Per-project work areas (Figma: New project wizard — "Sites" step). A project can have several of these, each
 *  with its own assigned engineer and shift; daily reports themselves stay scoped to the project (not re-pointed
 *  to a site) — this is metadata only, used for Equipment's "current site" assignment and project-level clarity. */
export const WorkAreasPage = () => {
  const { project, isManager } = useProject()
  const { data: sites, error, loading, reload, setData } = useAsync(() => api.fetchSites(project.id), [project.id])
  const { data: members } = useAsync(() => api.fetchMembers(project.id), [project.id])
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Site | null>(null)

  const engineers = (members ?? [])
    .filter((member) => member.role === 'SITE_ENGINEER')
    .map((member) => ({ id: member.user.id, name: member.user.name }))

  const create = async (input: SiteInput) => {
    const created = await run('add', () => api.createSite(project.id, input), t('تمت إضافة منطقة العمل', 'Work area added'))
    if (created) {
      setData((current) => [...(current ?? []), created])
      setAdding(false)
    }
  }

  const update = async (input: SiteInput) => {
    if (!editing) return
    const updated = await run(`edit${editing.id}`, () => api.updateSite(editing.id, input), t('تم حفظ التعديلات', 'Changes saved'))
    if (updated) {
      setData((current) => current?.map((item) => (item.id === updated.id ? updated : item)))
      setEditing(null)
    }
  }

  const remove = async (item: Site) => {
    if (!(await confirm(t(`حذف «${item.name}» من مناطق عمل المشروع؟`, `Delete "${item.name}" from the project's work areas?`), { confirmLabel: t('حذف', 'Delete') }))) return
    const done = await run(`rm${item.id}`, () => api.removeSite(item.id), t('تم الحذف', 'Deleted'))
    if (done) setData((current) => current?.filter((entry) => entry.id !== item.id))
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('مناطق العمل', 'Work areas')}</h1>
          <p className="muted">{t('أجزاء المشروع التي يُسند لها مهندسون ووردية عمل', 'Parts of the project with an assigned engineer and shift')}</p>
        </div>
        {isManager ? (
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('إضافة منطقة عمل', 'Add work area')}
          </Button>
        ) : null}
      </div>

      <Card>
        {loading && !sites ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !sites?.length ? (
          <EmptyState title={t('لا توجد مناطق عمل مسجّلة بعد', 'No work areas recorded yet')} icon="mapPin">
            {isManager ? (
              <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                {t('إضافة منطقة عمل', 'Add work area')}
              </Button>
            ) : null}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('المنطقة', 'Area')}</th>
                  <th scope="col">{t('النطاق', 'Scope')}</th>
                  <th scope="col">{t('المهندس', 'Engineer')}</th>
                  <th scope="col">{t('الوردية', 'Shift')}</th>
                  <th scope="col">{t('الحالة', 'Status')}</th>
                  {isManager ? (
                    <th scope="col">
                      <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.id}>
                    <td>
                      <strong>{site.name}</strong>
                    </td>
                    <td>{site.scope || <span className="muted">—</span>}</td>
                    <td>{site.engineerName || <span className="muted">{t('غير مُسند', 'Unassigned')}</span>}</td>
                    <td>{site.shift || <span className="muted">—</span>}</td>
                    <td>
                      <span className={STATUS_BADGE_CLASS[site.status]}>{siteStatusLabel(site.status)}</span>
                    </td>
                    {isManager ? (
                      <td>
                        <div className="row-actions">
                          <IconButton icon="edit" label={t(`تعديل ${site.name}`, `Edit ${site.name}`)} onClick={() => setEditing(site)} />
                          <IconButton
                            icon="trash"
                            label={t(`حذف ${site.name}`, `Delete ${site.name}`)}
                            onClick={() => remove(site)}
                            disabled={busy === `rm${site.id}`}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {adding ? <SiteForm engineers={engineers} busy={busy === 'add'} onCancel={() => setAdding(false)} onSubmit={create} /> : null}
      {editing ? (
        <SiteForm
          initial={editing}
          engineers={engineers}
          busy={busy === `edit${editing.id}`}
          onCancel={() => setEditing(null)}
          onSubmit={update}
        />
      ) : null}
    </div>
  )
}
